// Shared option-list cache for the portal's pickers.
//
// Every FieldSelect/MultiFieldSelect fetched its whole option list on mount and
// again on every locality change, throwing the previous list away first. Opening
// the incident form fired four requests; changing locality fired three more; and
// each one parked the picker on "Loading data please wait..." over data the
// browser had already seen moments earlier. The lists themselves are small
// reference data — on staging, 4 localities, 21 sub-localities, 94 field options
// and at most ~500 units for the largest locality — so the round trip, not the
// filtering, is what the admin actually waits on.
//
// Entries are keyed by the exact request, so a locality-scoped list stays
// distinct from another locality's. They expire after TTL_MS, which is what
// keeps an option created elsewhere in the portal from staying invisible;
// invalidate() exists for the cases that shouldn't wait that long.
//
// Concurrent callers share a single in-flight request. That alone removes the
// duplicate fetches two pickers pointed at the same list used to make.
import { api } from "includes";

const TTL_MS = 5 * 60 * 1000;

type Entry = { at: number; rows: any[] };

const cache = new Map<string, Entry>();
const inflight = new Map<string, Promise<any[]>>();

const keyOf = (url: string, query: string, auth: string) =>
  `${auth}|${url}|${query || ""}`;

const fresh = (e: Entry | undefined): e is Entry =>
  Boolean(e) && Date.now() - (e as Entry).at < TTL_MS;

/** Cached rows if we already hold them, else undefined. Lets a caller skip its
 *  loading state entirely rather than flashing one for a resolved promise. */
export function peekOptions(
  url: string,
  query: string,
  auth: string
): any[] | undefined {
  const hit = cache.get(keyOf(url, query, auth));
  return fresh(hit) ? hit.rows : undefined;
}

export function fetchOptions(
  url: string,
  query: string,
  auth: string
): Promise<any[]> {
  const key = keyOf(url, query, auth);

  const hit = cache.get(key);
  if (fresh(hit)) return Promise.resolve(hit.rows);

  const pending = inflight.get(key);
  if (pending) return pending;

  const request = new Promise<any[]>((resolve) => {
    api.call({ url, method: "GET", auth, query }, (res: any) => {
      inflight.delete(key);
      if (res.status === 200) {
        const rows = res.data?.data || [];
        cache.set(key, { at: Date.now(), rows });
        resolve(rows);
      } else {
        // A failed list is not cached — the next mount should try again rather
        // than serve an empty picker for the whole TTL.
        resolve([]);
      }
    });
  });

  inflight.set(key, request);
  return request;
}

/** Drop cached lists so freshly created options appear without waiting out the
 *  TTL. Pass a url fragment to target one resource, or nothing to clear all. */
export function invalidateOptions(urlFragment?: string) {
  if (!urlFragment) {
    cache.clear();
    return;
  }
  Array.from(cache.keys())
    .filter((k) => k.includes(urlFragment))
    .forEach((k) => cache.delete(k));
}
